import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { profileAPI, uploadAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    User, 
    Briefcase, 
    Building2, 
    MapPin, 
    Save,
    Camera,
    Loader2,
    ScrollText,
    CheckCircle,
    Clock
} from 'lucide-react';

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming"
];

const ProfilePage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    
    const [profile, setProfile] = useState({
        name: '',
        title: '',
        organization: '',
        city: '',
        state: '',
        bio: '',
        picture: '',
    });

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/');
            return;
        }

        if (isAuthenticated) {
            fetchProfile();
        }
    }, [isAuthenticated, authLoading, navigate]);

    const fetchProfile = async () => {
        try {
            const response = await profileAPI.getMyProfile();
            setProfile({
                name: response.data.name || '',
                title: response.data.title || '',
                organization: response.data.organization || '',
                city: response.data.city || '',
                state: response.data.state || '',
                bio: response.data.bio || '',
                picture: response.data.picture || '',
                terms_accepted: response.data.terms_accepted || false,
                terms_accepted_at: response.data.terms_accepted_at || null,
            });
        } catch (error) {
            console.error('Error fetching profile:', error);
            toast.error('Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setProfile(prev => ({ ...prev, [field]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file');
            return;
        }

        setUploading(true);
        try {
            const response = await uploadAPI.upload(file);
            setProfile(prev => ({ ...prev, picture: response.data.url }));
            toast.success('Image uploaded');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await profileAPI.updateMyProfile(profile);
            toast.success('Profile updated successfully');
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-background">
                <div className="max-w-2xl mx-auto px-4 py-12">
                    <Skeleton className="h-10 w-48 mb-8" />
                    <Card>
                        <CardContent className="p-6 space-y-6">
                            <Skeleton className="h-24 w-24 rounded-full mx-auto" />
                            <Skeleton className="h-10 w-full" />
                            <Skeleton className="h-10 w-full" />
                            <Skeleton className="h-10 w-full" />
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="profile-page">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <h1 className="font-heading text-3xl font-bold mb-8 flex items-center gap-3">
                    <User className="h-8 w-8 text-primary" />
                    My Profile
                </h1>

                <Card>
                    <CardHeader>
                        <CardTitle>Profile Information</CardTitle>
                        <CardDescription>
                            This information will be visible to other HAAPE members
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Profile Picture */}
                        <div className="flex flex-col items-center gap-4">
                            <div className="relative">
                                <Avatar className="h-24 w-24">
                                    <AvatarImage src={profile.picture} alt={profile.name} />
                                    <AvatarFallback className="text-2xl">
                                        {profile.name?.charAt(0) || 'U'}
                                    </AvatarFallback>
                                </Avatar>
                                <label 
                                    htmlFor="picture-upload"
                                    className="absolute bottom-0 right-0 p-1.5 bg-primary text-primary-foreground rounded-full cursor-pointer hover:bg-primary/90 transition-colors"
                                >
                                    {uploading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Camera className="h-4 w-4" />
                                    )}
                                </label>
                                <input
                                    id="picture-upload"
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageUpload}
                                    disabled={uploading}
                                />
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Click the camera icon to upload a profile picture
                            </p>
                        </div>

                        {/* Name */}
                        <div className="space-y-2">
                            <Label htmlFor="name" className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Full Name *
                            </Label>
                            <Input
                                id="name"
                                value={profile.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="Your full name"
                                data-testid="input-name"
                            />
                        </div>

                        {/* Title */}
                        <div className="space-y-2">
                            <Label htmlFor="title" className="flex items-center gap-2">
                                <Briefcase className="h-4 w-4" />
                                Title *
                            </Label>
                            <Input
                                id="title"
                                value={profile.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                placeholder="e.g., Program Director, Career Coach"
                                data-testid="input-title"
                            />
                        </div>

                        {/* Organization */}
                        <div className="space-y-2">
                            <Label htmlFor="organization" className="flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                Organization *
                            </Label>
                            <Input
                                id="organization"
                                value={profile.organization}
                                onChange={(e) => handleChange('organization', e.target.value)}
                                placeholder="Your organization name"
                                data-testid="input-organization"
                            />
                        </div>

                        {/* Location */}
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="city" className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    City *
                                </Label>
                                <Input
                                    id="city"
                                    value={profile.city}
                                    onChange={(e) => handleChange('city', e.target.value)}
                                    placeholder="Your city"
                                    data-testid="input-city"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="state">State *</Label>
                                <Select 
                                    value={profile.state} 
                                    onValueChange={(v) => handleChange('state', v)}
                                >
                                    <SelectTrigger data-testid="select-state">
                                        <SelectValue placeholder="Select state" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {US_STATES.map((state) => (
                                            <SelectItem key={state} value={state}>{state}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Bio */}
                        <div className="space-y-2">
                            <Label htmlFor="bio">Bio (Optional)</Label>
                            <Textarea
                                id="bio"
                                value={profile.bio}
                                onChange={(e) => handleChange('bio', e.target.value)}
                                placeholder="Tell other members about yourself..."
                                rows={4}
                                data-testid="input-bio"
                            />
                        </div>

                        {/* Terms & Conditions Status */}
                        <div className="border rounded-lg p-4 bg-muted/30" data-testid="terms-status-section">
                            <Label className="flex items-center gap-2 mb-2">
                                <ScrollText className="h-4 w-4" />
                                Terms & Conditions
                            </Label>
                            {profile.terms_accepted ? (
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                                    <span className="text-sm text-emerald-700 font-medium">
                                        Accepted on {profile.terms_accepted_at ? new Date(profile.terms_accepted_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'record available'}
                                    </span>
                                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline ml-auto" data-testid="view-terms-from-profile">
                                        View Terms
                                    </a>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-amber-600" />
                                    <span className="text-sm text-amber-700">Not yet accepted</span>
                                    <a href="/terms/accept" className="text-xs text-primary hover:underline ml-auto">Accept Terms</a>
                                </div>
                            )}
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end pt-4">
                            <Button 
                                onClick={handleSave} 
                                disabled={saving}
                                className="gap-2"
                                data-testid="save-profile-btn"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4" />
                                        Save Profile
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ProfilePage;
